/**
 * FreeActions — the "Aha!" freemium funnel.
 *
 * Three Copilot actions any guest can run without signing up or adding
 * a credit card:
 *   1. `screen`   — instant tenant-screening summary from applicant data
 *   2. `lease`    — 60-second bulletproof province-specific lease preview
 *   3. `listing`  — one-tap listing rewrite for Zillow / Kijiji / Realtor.ca
 *
 * A localStorage counter tracks how many actions the visitor has used.
 * On the 3rd (or after any success), the "sign up to continue" CTA
 * appears. Nothing hits the backend behind auth — everything uses the
 * public endpoints already exposed by server.py, which fall back to a
 * template response if LLM budget is exhausted (see _smart_engine_soft_handler).
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Zap, CheckCircle2, ArrowRight, Sparkles, Copy, Download, ShieldCheck, FileText, Home, UserCheck } from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";

import { friendlyError } from "@/lib/friendlyError";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const COUNTER_KEY = "crc_free_actions_used";

const TOOLS = [
  {
    id: "screen", icon: UserCheck, label: "Tenant screening summary",
    tagline: "Paste an applicant profile — get a 20-second decision-ready summary.",
    placeholder: "Applicant: Sarah Chen. Credit: 720. Income: $6,400/mo. Job: 3 yr at same employer. References: 2 positive prior landlords. Move-in target: Oct 1.",
    endpoint: "/api/copilot/assist",
    body: (text) => ({
      text,
      mode: "expand",
      context: "generating a Canadian tenant screening brief (<= 140 words) with credit assessment, income-to-rent ratio, employment stability, references, and a clear recommend/hold/reject verdict. Neutral, evidence-based tone.",
    }),
    outputLabel: "Screening summary",
  },
  {
    id: "lease", icon: FileText, label: "60-second lease preview",
    tagline: "Describe the deal — get a province-compliant lease preview.",
    placeholder: "Ontario · 456 Bloor West · monthly $2,600 · lease Sept 1 2026 to Aug 31 2027 · deposit last month $2,600 · tenant Alex Wong · smoking prohibited · pets: one small dog allowed",
    endpoint: "/api/copilot/assist",
    body: (text) => ({
      text,
      mode: "expand",
      context: "generating a Canadian residential lease PREVIEW — a Preamble paragraph + three critical clauses (Term, Rent, Security Deposit) written in the exact statutory language of the detected province (Ontario RTA, BC RTB, Alberta RTDRS, Quebec Loi 41 bail bilingue). Preview only — mention the full signed lease unlocks after signup.",
    }),
    outputLabel: "Lease preview (Term + Rent + Deposit)",
  },
  {
    id: "listing", icon: Home, label: "Listing optimizer for Zillow / Kijiji",
    tagline: "Paste your current listing — get a click-optimized rewrite.",
    placeholder: "1-bed 1-bath apartment in downtown Toronto, close to subway. $2,300 per month. Available October. Contact for viewing.",
    endpoint: "/api/copilot/assist",
    body: (text) => ({
      text,
      mode: "rewrite",
      context: "CTR-optimized Canadian rental listing rewrite for Zillow / Realtor.ca / Kijiji: a punchy 60-character headline with 1 emoji, 3 bullet features, 40-word body. Front-load high-intent phrases (bright, walkable, near subway, move-in ready). Return the full rewritten listing.",
    }),
    outputLabel: "Optimized listing (headline + bullets + body)",
  },
];

export default function FreeActions() {
  const [params, setParams] = useSearchParams();
  const activeTool = params.get("tool") || TOOLS[0].id;
  const tool = TOOLS.find((t) => t.id === activeTool) || TOOLS[0];

  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [usedCount, setUsedCount] = useState(() => {
    try { return parseInt(localStorage.getItem(COUNTER_KEY) || "0", 10) || 0; } catch { return 0; }
  });

  useEffect(() => {
    document.title = "3 free Copilot actions — no card · Central Rentals Canada";
    setOutput("");
    setErr("");
    setInput("");
  }, [activeTool]);

  const remaining = Math.max(0, 3 - usedCount);
  const capped = usedCount >= 3;

  const run = async () => {
    if (!input.trim()) return;
    if (capped) return;
    setBusy(true);
    setErr("");
    setOutput("");
    try {
      const r = await fetch(`${API}${tool.endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(tool.body(input.trim())),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok && !(j?.text || j?.body)) {
        setErr(friendlyError({ response: { status: r.status, data: j } }, "Copilot is briefly unavailable. Try again in a moment."));
      } else {
        const text = j.text || j.body || j.draft || "";
        // Distinguish real Copilot output from the graceful fallback (which
        // just echoes the input while credits are propagating). The
        // banner above the output makes the state obvious to the user.
        if (j.source === "fallback" || (text && text.trim() === input.trim())) {
          setOutput("");
          setErr("Copilot credits are topping up — real output typically returns within a minute. Please try again in ~60 seconds, or explore the paid plans below.");
        } else {
          setOutput(text || "No changes suggested — your input already looks great.");
          const next = usedCount + 1;
          setUsedCount(next);
          try { localStorage.setItem(COUNTER_KEY, String(next)); } catch { /* private mode */ }
        }
      }
    } catch (e) {
      setErr(friendlyError(e, "Copilot is briefly unavailable. Try again in a moment."));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(output); } catch { /* ignore */ }
  };

  const banner = useMemo(() => {
    if (capped) return { tone: "amber", text: "You've used all 3 free actions. Sign up (still no card) to unlock unlimited Copilot." };
    if (remaining === 3) return { tone: "emerald", text: "You have 3 free Copilot actions. No sign-up needed to try." };
    return { tone: "emerald", text: `${remaining} free action${remaining === 1 ? "" : "s"} left — no card required.` };
  }, [remaining, capped]);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="free-actions-page">
      <PublicHeader />

      <section className="max-w-4xl mx-auto px-6 pt-10 pb-6 text-center">
        <p className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold border border-emerald-100">
          <Sparkles className="w-3.5 h-3.5" /> The Aha! moment
        </p>
        <h1 className="mt-3 text-3xl sm:text-4xl font-black tracking-tighter font-display" data-testid="free-actions-h1">
          3 free Copilot actions. Cancel any time.
        </h1>
        <p className="mt-3 text-slate-600 max-w-2xl mx-auto">
          Try the actual Copilot on your own data — a screening brief, a bulletproof lease, a click-optimized listing. Sign up only if you like what you see.
        </p>
      </section>

      <section className={`max-w-4xl mx-auto px-6`}>
        <div className={`p-3 rounded-lg text-sm font-medium border text-center ${banner.tone === "amber" ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-emerald-50 border-emerald-200 text-emerald-800"}`} data-testid="free-actions-usage-banner">
          {banner.text}
        </div>
      </section>

      {/* Tool selector */}
      <section className="max-w-4xl mx-auto px-6 pt-6 grid sm:grid-cols-3 gap-3" data-testid="free-actions-tabs">
        {TOOLS.map((t) => (
          <button
            key={t.id}
            data-testid={`free-actions-tab-${t.id}`}
            onClick={() => setParams({ tool: t.id })}
            className={`text-left p-4 rounded-xl border transition-all ${activeTool === t.id ? "bg-white border-emerald-500 shadow-lg shadow-emerald-500/10" : "bg-white border-slate-200 hover:border-slate-300"}`}
          >
            <t.icon className={`w-5 h-5 mb-2 ${activeTool === t.id ? "text-emerald-600" : "text-slate-500"}`} />
            <div className="font-bold text-sm text-slate-900">{t.label}</div>
            <div className="text-xs text-slate-500 mt-1">{t.tagline}</div>
          </button>
        ))}
      </section>

      {/* IO panel */}
      <section className="max-w-4xl mx-auto px-6 py-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-7 shadow-sm" data-testid="free-actions-io">
          <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">
            Your input
          </label>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={tool.placeholder}
            className="w-full min-h-[140px] p-3 border border-slate-200 rounded-lg text-sm bg-slate-50 focus:bg-white focus:border-emerald-500 focus:outline-none"
            data-testid="free-actions-input"
            disabled={busy || capped}
          />
          <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
            <button
              onClick={run}
              disabled={busy || capped || !input.trim()}
              data-testid="free-actions-run-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Zap className="w-4 h-4" /> {busy ? "Copilot thinking…" : capped ? "Sign up to continue" : `Run this Copilot (${remaining} left)`}
            </button>
            <div className="text-xs text-slate-500 flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /> Not stored · no signup · no card
            </div>
          </div>

          {err && (
            <div className="mt-4 p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-sm" data-testid="free-actions-err">
              {err}
            </div>
          )}

          {output && (
            <div className="mt-6" data-testid="free-actions-output-wrap">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">
                  {tool.outputLabel}
                </label>
                <div className="flex items-center gap-2">
                  <button onClick={copy} data-testid="free-actions-copy" className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900">
                    <Copy className="w-3.5 h-3.5" /> Copy
                  </button>
                  <a
                    href={`data:text/plain;charset=utf-8,${encodeURIComponent(output)}`}
                    download={`central-rentals-${tool.id}-preview.txt`}
                    className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900"
                    data-testid="free-actions-download"
                  >
                    <Download className="w-3.5 h-3.5" /> Download
                  </a>
                </div>
              </div>
              <div className="p-4 rounded-lg bg-slate-900 text-slate-100 text-sm font-mono whitespace-pre-wrap break-words leading-relaxed" data-testid="free-actions-output">
                {output}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Conversion CTA */}
      <section className="max-w-4xl mx-auto px-6 pb-16">
        <div className={`p-6 sm:p-8 rounded-2xl bg-gradient-to-br from-emerald-600 to-emerald-800 text-white shadow-xl ${capped ? "" : "opacity-95"}`}>
          <div className="grid sm:grid-cols-[1fr,auto] gap-4 items-center">
            <div>
              <p className="text-emerald-100 text-xs font-bold uppercase tracking-widest">Convert your preview into the full app</p>
              <h3 className="mt-1 text-2xl font-black tracking-tighter">Sign up free — no card, unlimited Copilot for 14 days.</h3>
              <ul className="mt-2 text-sm text-emerald-50 space-y-1">
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Save your generated leases + reports</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Full rent-collection + tenant portal</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Cancel any time — Klarna Pay-in-4 available</li>
              </ul>
            </div>
            <Link
              to="/signup"
              data-testid="free-actions-signup-cta"
              className="inline-flex items-center justify-center gap-1.5 px-6 py-3 rounded-xl bg-white text-emerald-800 font-black text-sm hover:bg-emerald-50 transition-colors whitespace-nowrap"
            >
              Sign up free <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      
    </div>
  );
}
